import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Button,
  Tooltip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Menu from '../components/Menu';
import { useAuth } from '../hooks/useAuth';
import { isAdminOrSuperAdmin } from '../utils/roles';
import api from '../utils/apiClient';

// Стили для скрытия стрелок в числовых полях
const numberInputStyle = {
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
};

// Список доступных алгоритмов
const ALGORITHMS = [
  'SHA-256',
  'Scrypt',
  'X11',
  'Sia',
  'Kaspa',
  'Equihash'
];

// Список единиц измерения хешрейта
const HASHRATE_UNITS = [
  'H/s',
  'KH/s',
  'MH/s',
  'GH/s',
  'TH/s',
  'PH/s',
  'EH/s'
];

const ModelsSettings = () => {
  const { user } = useAuth();
  const { t } = useTranslation('admin');
  const isAdmin = isAdminOrSuperAdmin(user?.role);

  // Состояния для хостингов
  const [hostings, setHostings] = useState([]);
  const [selectedHostingId, setSelectedHostingId] = useState('');

  // Состояния для моделей
  const [models, setModels] = useState([]);
  const [hostingSettings, setHostingSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Состояния для редактирования
  const [editingId, setEditingId] = useState(null);
  const [editedModel, setEditedModel] = useState(null);
  const [editedHostingSetting, setEditedHostingSetting] = useState(null);

  // Состояния для диалога добавления
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newModel, setNewModel] = useState({
    model: '',
    algorithm: 'SHA-256',
    hashrate_unit: 'TH/s',
    hashrate_min: '',
    hashrate_max: '',
    temperature_min: 0,
    temperature_max: 85,
    fan_speed_min: 2500,
    fan_speed_max: 8000,
    contract_amount: '',
    power: ''
  });

  // Загрузка списка хостингов (только для админа)
  const loadHostings = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('data/hosting');
      setHostings(response.data.results || []);
      if (response.data.results?.length > 0 && !selectedHostingId) {
        setSelectedHostingId(response.data.results[0].id);
      }
    } catch (error) {
      console.error('Failed to load hostings:', error);
    }
  }, [isAdmin, selectedHostingId]);

  // Загрузка моделей и настроек хостинга
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Загружаем модели
      const modelsResponse = await api.get('data/models-settings');
      const modelsData = modelsResponse.data.results || modelsResponse.data || [];
      setModels(modelsData);

      // Загружаем настройки хостинга
      let hostingUrl = 'data/models-hosting-settings';
      if (isAdmin && selectedHostingId) {
        hostingUrl += `?hosting_id=${selectedHostingId}`;
      }
      const hostingResponse = await api.get(hostingUrl);
      const hostingData = hostingResponse.data.results || hostingResponse.data || [];
      setHostingSettings(hostingData);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError(t('modelsErrorLoadData'));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedHostingId]);

  // Начальная загрузка
  useEffect(() => {
    if (user) {
      loadHostings();
    }
  }, [user, loadHostings]);

  // Загрузка данных при смене хостинга
  useEffect(() => {
    if (user && (isAdmin ? selectedHostingId : true)) {
      loadData();
    }
  }, [user, isAdmin, selectedHostingId, loadData]);

  // Получить настройки хостинга для модели
  const getHostingSettingForModel = (modelId) => {
    return hostingSettings.find(hs => hs.model_id === modelId);
  };

  // === Обработчики редактирования ===

  const handleEdit = (model) => {
    setEditingId(model.id);
    setEditedModel({ ...model });
    const hostingSetting = getHostingSettingForModel(model.id);
    setEditedHostingSetting(hostingSetting ? { ...hostingSetting } : {
      model_id: model.id,
      contract_amount: '',
      power: ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedModel(null);
    setEditedHostingSetting(null);
  };

  const handleSaveEdit = async () => {
    try {
      // Сохраняем модель (только админ)
      if (isAdmin) {
        await api.patch(`data/models-settings/${editingId}`, {
          model: editedModel.model,
          algorithm: editedModel.algorithm,
          hashrate_unit: editedModel.hashrate_unit,
          hashrate_min: Number(editedModel.hashrate_min),
          hashrate_max: Number(editedModel.hashrate_max),
          temperature_min: Number(editedModel.temperature_min),
          temperature_max: Number(editedModel.temperature_max),
          fan_speed_min: Number(editedModel.fan_speed_min),
          fan_speed_max: Number(editedModel.fan_speed_max)
        });
      }

      // Сохраняем/создаём настройки хостинга
      const existingHostingSetting = getHostingSettingForModel(editingId);

      if (editedHostingSetting.contract_amount || editedHostingSetting.power) {
        const hostingData = {
          contract_amount: Number(editedHostingSetting.contract_amount) || 0,
          power: Number(editedHostingSetting.power) || 0
        };

        if (existingHostingSetting) {
          // Обновляем существующую запись
          await api.patch(`data/models-hosting-settings/${existingHostingSetting.id}`, hostingData);
        } else {
          // Создаём новую запись
          const createData = {
            model_id: editingId,
            ...hostingData
          };
          if (isAdmin && selectedHostingId) {
            createData.hosting_id = selectedHostingId;
          }
          await api.post('data/models-hosting-settings', createData);
        }
      }

      setEditingId(null);
      setEditedModel(null);
      setEditedHostingSetting(null);
      loadData();
    } catch (error) {
      console.error('Failed to save:', error);
      alert(t('modelsErrorSave'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('modelsDeleteConfirm'))) {
      try {
        // Удаляем настройки хостинга для этой модели
        const hostingSetting = getHostingSettingForModel(id);
        if (hostingSetting) {
          await api.delete(`data/models-hosting-settings/${hostingSetting.id}`);
        }
        // Удаляем модель
        await api.delete(`data/models-settings/${id}`);
        loadData();
      } catch (error) {
        console.error('Failed to delete:', error);
        alert(t('modelsErrorDelete'));
      }
    }
  };

  const handleEditChange = (field, value) => {
    if (field === 'contract_amount' || field === 'power') {
      setEditedHostingSetting({ ...editedHostingSetting, [field]: value });
    } else {
      setEditedModel({ ...editedModel, [field]: value });
    }
  };

  // === Обработчики добавления ===

  const handleOpenAddDialog = () => {
    setNewModel({
      model: '',
      algorithm: 'SHA-256',
      hashrate_unit: 'TH/s',
      hashrate_min: '',
      hashrate_max: '',
      temperature_min: 0,
      temperature_max: 85,
      fan_speed_min: 2500,
      fan_speed_max: 8000,
      contract_amount: '',
      power: ''
    });
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const handleAddModel = async () => {
    try {
      // Создаём модель
      const modelResponse = await api.post('data/models-settings', {
        model: newModel.model,
        algorithm: newModel.algorithm,
        hashrate_unit: newModel.hashrate_unit,
        hashrate_min: Number(newModel.hashrate_min),
        hashrate_max: Number(newModel.hashrate_max),
        temperature_min: Number(newModel.temperature_min),
        temperature_max: Number(newModel.temperature_max),
        fan_speed_min: Number(newModel.fan_speed_min),
        fan_speed_max: Number(newModel.fan_speed_max)
      });

      // Если указаны contract_amount или power, создаём настройку хостинга
      if (newModel.contract_amount || newModel.power) {
        const newModelId = modelResponse.data.id;
        const hostingData = {
          model_id: newModelId,
          contract_amount: Number(newModel.contract_amount) || 0,
          power: Number(newModel.power) || 0
        };
        if (isAdmin && selectedHostingId) {
          hostingData.hosting_id = selectedHostingId;
        }
        await api.post('data/models-hosting-settings', hostingData);
      }

      setAddDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to add model:', error);
      alert(t('modelsErrorAddModel'));
    }
  };

  const handleNewModelChange = (field, value) => {
    setNewModel({ ...newModel, [field]: value });
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Menu />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              {t('modelsTitle')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Выбор хостинга для админа */}
              {isAdmin && hostings.length > 0 && (
                <TextField
                  select
                  label={t('modelsHostingLabel')}
                  value={selectedHostingId}
                  onChange={(e) => setSelectedHostingId(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  {hostings.map((hosting) => (
                    <MenuItem key={hosting.id} value={hosting.id}>
                      {hosting.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleOpenAddDialog}
                >
                  {t('modelsAddModel')}
                </Button>
              )}
            </Box>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {!loading && !error && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { p: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('modelsColumnModel')}</TableCell>
                    <TableCell>{t('modelsColumnAlgorithm')}</TableCell>
                    <TableCell>{t('modelsColumnUnits')}</TableCell>
                    <TableCell>{t('modelsColumnHashrateMin')}</TableCell>
                    <TableCell>{t('modelsColumnHashrateMax')}</TableCell>
                    <TableCell>{t('modelsColumnTempMin')}</TableCell>
                    <TableCell>{t('modelsColumnTempMax')}</TableCell>
                    <TableCell>{t('modelsColumnFanMin')}</TableCell>
                    <TableCell>{t('modelsColumnFanMax')}</TableCell>
                    <TableCell>{t('modelsColumnAmount')}</TableCell>
                    <TableCell>{t('modelsColumnPower')}</TableCell>
                    <TableCell align="center" width={120}>{t('modelsColumnActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.map((model) => {
                    const isEditing = editingId === model.id;
                    const displayModel = isEditing ? editedModel : model;
                    const hostingSetting = getHostingSettingForModel(model.id);
                    const displayHostingSetting = isEditing ? editedHostingSetting : hostingSetting;

                    return (
                      <TableRow key={model.id} hover>
                        <TableCell width={180}>
                          {isEditing && isAdmin ? (
                            <TextField
                              value={displayModel.model}
                              onChange={(e) => handleEditChange('model', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          ) : (
                            displayModel.model
                          )}
                        </TableCell>
                        <TableCell width={120}>
                          {isEditing && isAdmin ? (
                            <TextField
                              select
                              value={displayModel.algorithm}
                              onChange={(e) => handleEditChange('algorithm', e.target.value)}
                              size="small"
                              fullWidth
                            >
                              {ALGORITHMS.map((algo) => (
                                <MenuItem key={algo} value={algo}>
                                  {algo}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            displayModel.algorithm
                          )}
                        </TableCell>
                        <TableCell width={100}>
                          {isEditing && isAdmin ? (
                            <TextField
                              select
                              value={displayModel.hashrate_unit}
                              onChange={(e) => handleEditChange('hashrate_unit', e.target.value)}
                              size="small"
                              fullWidth
                            >
                              {HASHRATE_UNITS.map((unit) => (
                                <MenuItem key={unit} value={unit}>
                                  {unit}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            displayModel.hashrate_unit
                          )}
                        </TableCell>
                        <TableCell width={100}>
                          {isEditing && isAdmin ? (
                            <TextField
                              value={displayModel.hashrate_min}
                              onChange={(e) => handleEditChange('hashrate_min', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayModel.hashrate_min
                          )}
                        </TableCell>
                        <TableCell width={100}>
                          {isEditing && isAdmin ? (
                            <TextField
                              value={displayModel.hashrate_max}
                              onChange={(e) => handleEditChange('hashrate_max', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayModel.hashrate_max
                          )}
                        </TableCell>
                        <TableCell width={100}>
                          {isEditing && isAdmin ? (
                            <TextField
                              value={displayModel.temperature_min}
                              onChange={(e) => handleEditChange('temperature_min', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayModel.temperature_min
                          )}
                        </TableCell>
                        <TableCell width={100}>
                          {isEditing && isAdmin ? (
                            <TextField
                              value={displayModel.temperature_max}
                              onChange={(e) => handleEditChange('temperature_max', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayModel.temperature_max
                          )}
                        </TableCell>
                        <TableCell width={100}>
                          {isEditing && isAdmin ? (
                            <TextField
                              value={displayModel.fan_speed_min}
                              onChange={(e) => handleEditChange('fan_speed_min', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayModel.fan_speed_min
                          )}
                        </TableCell>
                        <TableCell width={100}>
                          {isEditing && isAdmin ? (
                            <TextField
                              value={displayModel.fan_speed_max}
                              onChange={(e) => handleEditChange('fan_speed_max', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayModel.fan_speed_max
                          )}
                        </TableCell>
                        <TableCell width={120}>
                          {isEditing ? (
                            <TextField
                              value={displayHostingSetting?.contract_amount || ''}
                              onChange={(e) => handleEditChange('contract_amount', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayHostingSetting?.contract_amount || '—'
                          )}
                        </TableCell>
                        <TableCell width={120}>
                          {isEditing ? (
                            <TextField
                              value={displayHostingSetting?.power || ''}
                              onChange={(e) => handleEditChange('power', e.target.value)}
                              size="small"
                              type="number"
                              sx={numberInputStyle}
                              fullWidth
                            />
                          ) : (
                            displayHostingSetting?.power || '—'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isEditing ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title={t('modelsTooltipSave')}>
                                <IconButton color="success" size="small" onClick={handleSaveEdit}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('modelsTooltipCancel')}>
                                <IconButton color="error" size="small" onClick={handleCancelEdit}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title={t('modelsTooltipEdit')}>
                                <IconButton color="primary" size="small" onClick={() => handleEdit(model)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {isAdmin && (
                                <Tooltip title={t('modelsTooltipDelete')}>
                                  <IconButton color="error" size="small" onClick={() => handleDelete(model.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {models.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        {t('modelsNoData')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Диалог добавления новой модели */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>{t('modelsDialogAddTitle')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label={t('modelsFieldModel')}
              value={newModel.model}
              onChange={(e) => handleNewModelChange('model', e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              select
              label={t('modelsFieldAlgorithm')}
              value={newModel.algorithm}
              onChange={(e) => handleNewModelChange('algorithm', e.target.value)}
              size="small"
              fullWidth
            >
              {ALGORITHMS.map((algo) => (
                <MenuItem key={algo} value={algo}>
                  {algo}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('modelsFieldUnits')}
              value={newModel.hashrate_unit}
              onChange={(e) => handleNewModelChange('hashrate_unit', e.target.value)}
              size="small"
              fullWidth
            >
              {HASHRATE_UNITS.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('modelsFieldHashrateMin')}
              value={newModel.hashrate_min}
              onChange={(e) => handleNewModelChange('hashrate_min', e.target.value)}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
            <TextField
              label={t('modelsFieldHashrateMax')}
              value={newModel.hashrate_max}
              onChange={(e) => handleNewModelChange('hashrate_max', e.target.value)}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
            <TextField
              label={t('modelsFieldTempMin')}
              value={newModel.temperature_min}
              onChange={(e) => handleNewModelChange('temperature_min', Number(e.target.value))}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
            <TextField
              label={t('modelsFieldTempMax')}
              value={newModel.temperature_max}
              onChange={(e) => handleNewModelChange('temperature_max', Number(e.target.value))}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
            <TextField
              label={t('modelsFieldFanMin')}
              value={newModel.fan_speed_min}
              onChange={(e) => handleNewModelChange('fan_speed_min', Number(e.target.value))}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
            <TextField
              label={t('modelsFieldFanMax')}
              value={newModel.fan_speed_max}
              onChange={(e) => handleNewModelChange('fan_speed_max', Number(e.target.value))}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
            <TextField
              label={t('modelsFieldAmount')}
              value={newModel.contract_amount}
              onChange={(e) => handleNewModelChange('contract_amount', e.target.value)}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
            <TextField
              label={t('modelsFieldPower')}
              value={newModel.power}
              onChange={(e) => handleNewModelChange('power', e.target.value)}
              size="small"
              type="number"
              sx={numberInputStyle}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>{t('modelsTooltipCancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAddModel}
            disabled={!newModel.model || !newModel.hashrate_min || !newModel.hashrate_max}
          >
            {t('modelsAddModel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModelsSettings;
